/**
 * Easter door signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterDoorSignsPack: ToilePack = {
    slug: 'easter-door-signs',
    category: 'season',
    name: 'Easter door signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'welcome', label: 'Welcome', subject: 'A vertical door sign lettered "WELCOME" with a wreath of tulips below, a thick border'},
        {key: 'hunt', label: 'Egg hunt this way', subject: 'A vertical door sign lettered "EGG HUNT THIS WAY" in bold with an arrow and a basket motif'},
        {key: 'bunny', label: 'Bunny stop here', subject: 'A vertical door sign lettered "EASTER BUNNY STOP HERE" with a carrot, a thick border'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for the garden gate instead', 'in a friendlier, softer wording', 'as a hanging round sign'],
    directionHint: 'try: for the garden gate instead',
    seo: {
        title: 'Easter Door Signs: Welcome, Hunt & Bunny Stop',
        metaDescription: "Nine Easter door signs — a welcome sign, an egg-hunt arrow, and a bunny-stop sign — in pastel, vintage, and kids' styles, ready to print and hang.",
        h1: 'Easter door signs: welcome, egg hunt, bunny stop here',
        tagline: 'Nine signs, three messages, three styles each — lettered, bordered, ready to print.',
        intro: "Three Easter door signs — a welcome sign, an egg-hunt-this-way arrow, and a bunny-stop-here sign — each in pastel, vintage, and kids' cartoon styles. Every word prints exactly as lettered, bordered and ready to hang. Ask for a different message, a name added, or a matching yard-stake version, and the same sign grammar carries it.",
        useCases: [
            'a front-door Easter welcome',
            'an egg-hunt trail marker',
            'a classroom door display',
            'a community-event entrance sign',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each sign is a single vertical page, sized to read clearly at door height once printed.'},
            {q: 'Can I add a family name or change the wording?', a: 'Yes — the direction field takes any variation, including a family name or different wording.'},
            {q: 'How do I hang the sign?', a: 'The design prints as a flat vertical sign — tape, a suction hook, or a frame on your door handles the hanging; no hardware is included.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
